import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const customerPurchasesSlice = createSlice({
    name: "customerPurchases",
    initialState: {
        // Product Orders
        ordersPage: 0,
        ordersPageCount: 0,
        ordersList: null,
        ordersSummary: null,
        orderDetail: null,
        orderDetailLoading: false,
        // Redemption Orders
        redemptionPage: 0,
        redemptionPageCount: 0,
        redemptionList: null,
        redemptionSummary: null,
        redemptionDetail: null,
        redemptionDetailLoading: false,
        // Purchased Cards
        cardsPage: 0,
        cardsPageCount: 0,
        cardsList: null,
        cardsSummary: null,
    },
    reducers: {
        // Product Orders
        doGetCustomerOrdersSuccess: (state, action) => {
            state.ordersList = action.payload.data;
            state.ordersPage = action.payload?.current_page;
            state.ordersPageCount = action.payload?.page_count;
            state.ordersSummary = action.payload?.summary || null;
        },
        doGetCustomerOrdersFail: (state) => {
            state.ordersList = null;
            state.ordersPage = 0;
            state.ordersPageCount = 0;
            state.ordersSummary = null;
        },
        doGetCustomerOrderDetailStart: (state) => {
            state.orderDetailLoading = true;
        },
        doGetCustomerOrderDetailSuccess: (state, action) => {
            state.orderDetail = action.payload.data;
            state.orderDetailLoading = false;
        },
        doGetCustomerOrderDetailFail: (state) => {
            state.orderDetail = null;
            state.orderDetailLoading = false;
        },
        // Redemption Orders
        doGetCustomerRedemptionOrdersSuccess: (state, action) => {
            state.redemptionList = action.payload.data;
            state.redemptionPage = action.payload?.current_page;
            state.redemptionPageCount = action.payload?.page_count;
            state.redemptionSummary = action.payload?.summary || null;
        },
        doGetCustomerRedemptionOrdersFail: (state) => {
            state.redemptionList = null;
            state.redemptionPage = 0;
            state.redemptionPageCount = 0;
            state.redemptionSummary = null;
        },
        doGetCustomerRedemptionOrderDetailStart: (state) => {
            state.redemptionDetailLoading = true;
        },
        doGetCustomerRedemptionOrderDetailSuccess: (state, action) => {
            state.redemptionDetail = action.payload.data;
            state.redemptionDetailLoading = false;
        },
        doGetCustomerRedemptionOrderDetailFail: (state) => {
            state.redemptionDetail = null;
            state.redemptionDetailLoading = false;
        },
        // Purchased Cards
        doGetCustomerCardsSuccess: (state, action) => {
            state.cardsList = action.payload.data;
            state.cardsPage = action.payload?.current_page;
            state.cardsPageCount = action.payload?.page_count;
            state.cardsSummary = action.payload?.summary || null;
        },
        doGetCustomerCardsFail: (state) => {
            state.cardsList = null;
            state.cardsPage = 0;
            state.cardsPageCount = 0;
            state.cardsSummary = null;
        },
        resetCustomerPurchasesState: (state) => {
            state.ordersList = null;
            state.ordersPage = 0;
            state.ordersPageCount = 0;
            state.ordersSummary = null;
            state.orderDetail = null;
            state.orderDetailLoading = false;
            state.redemptionList = null;
            state.redemptionPage = 0;
            state.redemptionPageCount = 0;
            state.redemptionSummary = null;
            state.redemptionDetail = null;
            state.redemptionDetailLoading = false;
            state.cardsList = null;
            state.cardsPage = 0;
            state.cardsPageCount = 0;
            state.cardsSummary = null;
        },
    },
});

// Thunks
export const doGetCustomerOrders = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/customer-management/${param.customer_id}/orders?page=${param.page}&size=${param.limit}`;
        const response = await axios.get(url);
        dispatch(doGetCustomerOrdersSuccess(response.data));
    } catch (error) {
        dispatch(doGetCustomerOrdersFail());
    }
};

export const doGetCustomerOrderDetail = (param) => async (dispatch) => {
    try {
        dispatch(doGetCustomerOrderDetailStart());
        const url = `/api/admin/customer-management/${param.customer_id}/orders/${param.order_id}`;
        const response = await axios.get(url);
        dispatch(doGetCustomerOrderDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetCustomerOrderDetailFail());
    }
};

export const doGetCustomerRedemptionOrders = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/customer-management/${param.customer_id}/redemption-orders?page=${param.page}&size=${param.limit}`;
        const response = await axios.get(url);
        dispatch(doGetCustomerRedemptionOrdersSuccess(response.data));
    } catch (error) {
        dispatch(doGetCustomerRedemptionOrdersFail());
    }
};

export const doGetCustomerRedemptionOrderDetail = (param) => async (dispatch) => {
    try {
        dispatch(doGetCustomerRedemptionOrderDetailStart());
        const url = `/api/admin/customer-management/${param.customer_id}/redemption-orders/${param.order_id}`;
        const response = await axios.get(url);
        dispatch(doGetCustomerRedemptionOrderDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetCustomerRedemptionOrderDetailFail());
    }
};

export const doGetCustomerCards = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/customer-management/${param.customer_id}/cards?page=${param.page}&size=${param.limit}`;
        const response = await axios.get(url);
        dispatch(doGetCustomerCardsSuccess(response.data));
    } catch (error) {
        dispatch(doGetCustomerCardsFail());
    }
};

export const {
    doGetCustomerOrdersSuccess,
    doGetCustomerOrdersFail,
    doGetCustomerOrderDetailStart,
    doGetCustomerOrderDetailSuccess,
    doGetCustomerOrderDetailFail,
    doGetCustomerRedemptionOrdersSuccess,
    doGetCustomerRedemptionOrdersFail,
    doGetCustomerRedemptionOrderDetailStart,
    doGetCustomerRedemptionOrderDetailSuccess,
    doGetCustomerRedemptionOrderDetailFail,
    doGetCustomerCardsSuccess,
    doGetCustomerCardsFail,
    resetCustomerPurchasesState,
} = customerPurchasesSlice.actions;

// Selectors - Product Orders
export const getCustomerOrdersList = (state) => state.customerPurchases?.ordersList;
export const getCustomerOrdersPage = (state) => state.customerPurchases?.ordersPage;
export const getCustomerOrdersPageCount = (state) => state.customerPurchases?.ordersPageCount;
export const getCustomerOrdersSummary = (state) => state.customerPurchases?.ordersSummary;
export const getCustomerOrderDetail = (state) => state.customerPurchases?.orderDetail;
export const getCustomerOrderDetailLoading = (state) => state.customerPurchases?.orderDetailLoading;

// Selectors - Redemption Orders
export const getCustomerRedemptionOrdersList = (state) => state.customerPurchases?.redemptionList;
export const getCustomerRedemptionOrdersPage = (state) => state.customerPurchases?.redemptionPage;
export const getCustomerRedemptionOrdersPageCount = (state) => state.customerPurchases?.redemptionPageCount;
export const getCustomerRedemptionOrdersSummary = (state) => state.customerPurchases?.redemptionSummary;
export const getCustomerRedemptionOrderDetail = (state) => state.customerPurchases?.redemptionDetail;
export const getCustomerRedemptionOrderDetailLoading = (state) => state.customerPurchases?.redemptionDetailLoading;

// Selectors - Purchased Cards
export const getCustomerCardsList = (state) => state.customerPurchases?.cardsList;
export const getCustomerCardsPage = (state) => state.customerPurchases?.cardsPage;
export const getCustomerCardsPageCount = (state) => state.customerPurchases?.cardsPageCount;
export const getCustomerCardsSummary = (state) => state.customerPurchases?.cardsSummary;

export default customerPurchasesSlice.reducer;
